import { SocketClient } from "cbcore-ts"
import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { CBDataViewCellDescriptorWithObject } from "../Custom components/CB/CBDataView"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView } from "../Custom components/RH/RHDataView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHNotificationBadge } from "../Custom components/RH/RHNotificationBadge"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTime, formatRHDateTimeWithSecondsValue } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHColors, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHTimestampSorting } from "../Custom components/RH/Utils_slop/RHTimestampSorting"
import { RHEventLogViewTokens, RHLogEventViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import { AuditLogEntry } from "../SocketClientFunctions"
import { configureEventLogActionCell } from "./EventLogTableCellStyling"
import { LogEventViewController, registerCurrentLogEntry } from "./LogEventViewController"
import { LogViewController } from "./LogViewController"
import type { RHAuditEventData } from "./RHAuditEventData"

// ─────────────────────────────────────────────────────────────────────────────
// Flat row type shown in the data view
// ─────────────────────────────────────────────────────────────────────────────

type ProcurementTab = "details" | "team" | "makeOffer" | "offers" | "qa" | "contracts"

interface EventLogRow {
    _id: string
    /** Sortable ISO timestamp — the data view sorts the "Aeg" column by this raw
     *  field value as a string, so it must NOT be the localized display format
     *  (DD.MM.YYYY compares by day-of-month first and buries the newest rows). */
    timestamp: string
    /** Localized display text for the "Aeg" cell. */
    timestampText: string
    eventDescription: string
    procurementId: string
    /** Which tab in ProcurementDetailsViewController this event belongs to. */
    procurementTab?: ProcurementTab
    isSeen: boolean
    rawEntry: AuditLogEntry
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function eventTypeLabel(eventType: string): string {
    const label = RHLogEventViewTokens.eventTypeLabels[eventType]
    if (label) {
        return label
    }
    return "Tundmatu sündmus"
}

/** Returns the tab in ProcurementDetailsViewController most relevant to an event type. */
function procurementTabForEventType(eventType: string): ProcurementTab | undefined {
    switch (eventType) {
        case "procurement.published":
        case "procurement.created":
        case "procurement.modified":
        case "procurement.closed_without_contract":
        case "procurement.draft_cancelled":
        case "procurement.deadline_changed":
        case "procurement.deadline_auto_extended":
        case "procurement.deadline_passed":
        case "procurement.status_changed":
        case "procurement.tenderer_registered":
        case "procurement.deleted":
            return "details"
        
        case "procurement.team_updated":
        case "procurement.responsible_changed":
        case "procurement.self_added_to_team":
        case "procurement.tenderer_team_updated":
        case "offer.team_updated":
        case "offer.responsible_changed":
        case "offer.self_added_to_team":
            return "team"

        case "offer.submitted":
        case "offer.part_modified":
        case "offer.rescinded":
        case "offer.draft_created":
        case "offer.draft_modified":
            return "makeOffer"
        
        case "offer.winner_selected":
        case "offer.evaluation_finalized":
        case "offer.part_evaluated":
        case "offer.opened":
        case "offer.notification_sent":
        case "offer.evaluation_started":
            return "offers"
        
        case "question.asked":
        case "question.answered":
        case "question.deleted":
            return "qa"

        case "contract.created":
        case "contract.signed":
        case "contract.cancelled":
            return "contracts"
        
        default:
            return undefined
    }
}

function buildEventDescription(entry: AuditLogEntry): string {
    const data = entry.eventData as RHAuditEventData
    let procurementTitle: string | undefined
    if (typeof data.procurementTitle === "string" && data.procurementTitle.trim()) {
        procurementTitle = data.procurementTitle.trim()
    }
    let procurementObject = "hanke"
    let procurementLocation = ""
    if (procurementTitle) {
        procurementObject = `hanke „${procurementTitle}“`
        procurementLocation = ` hankes „${procurementTitle}“`
    }
    let partNumber: number | undefined
    if (
        typeof data.partNumber === "number" &&
        Number.isInteger(data.partNumber) &&
        data.partNumber > 0
    ) {
        partNumber = data.partNumber
    }
    const shouldShowPartIdentity = partNumber !== undefined &&
        RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(data.partCount)

    let companyName: string | undefined
    if (typeof data.companyName === "string" && data.companyName.trim()) {
        companyName = data.companyName.trim()
    }

    let userDisplayName: string | undefined
    if (typeof data.userDisplayName === "string" && data.userDisplayName.trim()) {
        userDisplayName = data.userDisplayName.trim()
    }
    
    switch (entry.eventType) {
        case "procurement.published":
            return `Avaldasid ${procurementObject}.`
        case "procurement.created":
            return `Alustasid ${procurementObject} ettevalmistamist.`
        case "procurement.modified":
            if (procurementTitle) {
                return `Muutsid ettevalmistamisel hanget „${procurementTitle}“.`
            }
            return "Muutsid ettevalmistamisel hanget."
        case "procurement.closed_without_contract":
            if (data.isReconciliationRound === YES) {
                if (procurementTitle) {
                    return `Lõpetasid täpsustava vooru „${procurementTitle}“ ilma lepinguta.`
                }
                return "Lõpetasid täpsustava vooru ilma lepinguta."
            }
            return `Lõpetasid ${procurementObject} ilma lepinguta.`
        case "procurement.draft_cancelled":
            return `Tühistasid ${procurementObject} koostamise.`
        case "procurement.deadline_changed": {
            const prev = data.previousDeadline
                         ? formatRHDateTime(new Date(data.previousDeadline))
                         : "teadmata"
            const next = data.newDeadline
                         ? formatRHDateTime(new Date(data.newDeadline))
                         : "teadmata"
            return `Muutsid hanke tähtaega: ${prev} → ${next}`
        }
        case "procurement.status_changed": {
            let previousStatus = "teadmata"
            if (typeof data.previousStatus === "string" && data.previousStatus.trim()) {
                previousStatus = data.previousStatus.trim()
            }
            let newStatus = "teadmata"
            if (typeof data.newStatus === "string" && data.newStatus.trim()) {
                newStatus = data.newStatus.trim()
            }
            return `Hanke staatus muutus: ${previousStatus} → ${newStatus}`
        }
        case "procurement.tenderer_registered":
            return `Registreerusid pakkujana${procurementLocation}.`
        case "offer.submitted":
            return `Esitasid pakkumuse${procurementLocation}.`
        case "offer.part_modified":
            return `Muutsid pakkumust${procurementLocation}.`
        case "offer.rescinded":
            return `Võtsid pakkumuse tagasi${procurementLocation}.`
        case "offer.opened":
            return `Avasid pakkumused${procurementLocation}.`
        case "offer.winner_selected":
            if (!shouldShowPartIdentity) {
                return `Märkisid ${procurementObject} pakkumuse edukaks.`
            }
            return `Märkisid ${procurementObject} osa ${partNumber} pakkumuse edukaks.`
        case "offer.evaluation_finalized":
            if (shouldShowPartIdentity) {
                return `Lõpetasid ${procurementObject} osa ${partNumber} hindamise ja saatsid teatised.`
            }
            return `Lõpetasid ${procurementObject} hindamise ja saatsid teatised.`
        case "offer.draft_created":
            return `Alustasid pakkumuse koostamist${procurementLocation}.`
        case "offer.part_evaluated":
            if (!shouldShowPartIdentity) {
                return `Hindasid pakkumust${procurementLocation}.`
            }
            return `Hindasid ${procurementObject} osa ${partNumber} pakkumust.`
        case "offer.notification_sent": {
            if (data.wasExcludedFromNextRound === YES) {
                return `Teavitasid pakkujat, et teda ei kutsutud järgmisse vooru${procurementLocation}.`
            }
            let procurementIdentity = ""
            let procurementName = ""
            if (procurementTitle) {
                procurementIdentity = ` hankes „${procurementTitle}“`
                procurementName = ` „${procurementTitle}“`
            }
            if (data.isWinner) {
                return `Teavitasid pakkujat, et tema pakkumus${procurementIdentity} tunnistati edukaks`
            }
            if (data.partHasWinner === NO) {
                if (shouldShowPartIdentity) {
                    return `Teavitasid pakkujat, et hanke${procurementName} osas ${partNumber} ` +
                        "ei tunnistatud ühtegi pakkumust edukaks"
                }
                if (procurementTitle) {
                    return `Teavitasid pakkujat, et hankes${procurementName} ` +
                        "ei tunnistatud ühtegi pakkumust edukaks"
                }
                return "Teavitasid pakkujat, et ühtegi pakkumust ei tunnistatud edukaks"
            }
            return `Teavitasid pakkujat, et tema pakkumust${procurementIdentity} ei tunnistatud edukaks`
        }
        case "question.asked":
            return `Esitasid küsimuse${procurementLocation}.`
        case "question.answered":
            return `Vastasid küsimusele${procurementLocation}.`
        case "question.deleted":
            return `Kustutasid küsimuse${procurementLocation}.`
        case "user.registered":
            return "Registreerusid kasutajaks"
        case "user.login":
            return "Logisid sisse"
        case "user.password_changed":
            return "Muutsid salasõna"
        case "company.user_invited":
            if (userDisplayName && companyName) {
                return `Kutsusid kasutaja ${userDisplayName} ` +
                    `ettevõtte „${companyName}“ kasutajaks.`
            }
            if (userDisplayName) {
                return `Kutsusid kasutaja ${userDisplayName} ettevõtte kasutajaks.`
            }
            if (companyName) {
                return `Kutsusid kasutaja ettevõtte „${companyName}“ kasutajaks.`
            }
            return "Kutsusid kasutaja ettevõtte kasutajaks."
        case "company.join_request_approved":
            if (userDisplayName && companyName) {
                return `Kinnitasid kasutaja ${userDisplayName} taotluse liituda ettevõttega „${companyName}“.`
            }
            if (companyName) {
                return `Kinnitasid taotluse liituda ettevõttega „${companyName}“.`
            }
            return "Kinnitasid ettevõttega liitumise taotluse."
        case "company.join_request_rejected":
            if (userDisplayName && companyName) {
                return `Lükkasid tagasi kasutaja ${userDisplayName} taotluse liituda ettevõttega „${companyName}“.`
            }
            if (companyName) {
                return `Lükkasid tagasi taotluse liituda ettevõttega „${companyName}“.`
            }
            return "Lükkasid ettevõttega liitumise taotluse tagasi."
        case "company.user_role_changed":
            if (userDisplayName && companyName) {
                return `Muutsid kasutaja ${userDisplayName} rolle ettevõttes „${companyName}“.`
            }
            if (companyName) {
                return `Muutsid kasutaja rolle ettevõttes „${companyName}“.`
            }
            return "Muutsid kasutaja rolle ettevõttes."
        case "company.user_removed":
            if (userDisplayName && companyName) {
                return `Eemaldasid kasutaja ${userDisplayName} ettevõttest „${companyName}“.`
            }
            if (companyName) {
                return `Eemaldasid kasutaja ettevõttest „${companyName}“.`
            }
            return "Eemaldasid kasutaja ettevõttest."
        case "file.uploaded":
            return `Laadisid üles faili${data.fileName ? ` „${data.fileName}“` : ""}`
        case "file.deleted":
            return `Kustutasid faili${data.fileName ? ` „${data.fileName}“` : ""}`
        case "contract.created":
            return "Loodi leping"
        case "contract.signed":
            if (
                procurementTitle &&
                shouldShowPartIdentity
            ) {
                return `Allkirjastasid lepingu ${procurementObject} osa ${partNumber} kohta.`
            }
            if (procurementTitle) {
                return `Allkirjastasid lepingu${procurementLocation}.`
            }
            return "Allkirjastasid lepingu."
        case "contract.cancelled":
            if (
                procurementTitle &&
                shouldShowPartIdentity
            ) {
                return `Märkisid, et ${procurementObject} osas ${partNumber} lepingut ei sõlmitud.`
            }
            if (procurementTitle) {
                return `Märkisid, et${procurementLocation} lepingut ei sõlmitud.`
            }
            return "Märkisid, et lepingut ei sõlmitud."
        default:
            return eventTypeLabel(entry.eventType)
    }
}

function flattenEntry(entry: AuditLogEntry): EventLogRow {
    const procurementId =
        (entry.target.type === "procurement" ? entry.target.id : "") ||
        (entry.eventData?.procurementId as string) || ""
    
    return {
        _id: entry._id,
        timestamp: RHTimestampSorting.sortableISOString(entry.timestamp),
        timestampText: formatRHDateTimeWithSecondsValue(entry.timestamp),
        eventDescription: RHTextSanitizer.escapedHTML(buildEventDescription(entry)),
        procurementId,
        procurementTab: procurementId ? procurementTabForEventType(entry.eventType) : undefined,
        isSeen: entry.isSeen,
        rawEntry: entry
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// View Controller
// ─────────────────────────────────────────────────────────────────────────────

export class EventLogViewController extends UIViewController {
    
    static override readonly routeComponentName = "eventLog"
    static override readonly ParameterIdentifierName = {}
    readonly titleKey = "eventLog"
    
    // ── Subviews ──────────────────────────────────────────────────────────────
    
    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        // UITextView.type.header2
        UITextView.type.header4
    ).configuredWithObject({
        text: "Minu tegevused",
        textColor: RHColors.textPrimary,
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)
    
    // Small pill showing „N uut" — hidden when unseenCount is 0
    readonly unseenBadgeLabel = new UITextView(
        this.view.elementID + "UnseenBadge"
    ).configuredWithObject({
        backgroundColor: RHColor.primary,
        textColor: RHColor.textWhite,
        isSingleLine: true,
        fontSize: 12
    }).addedAsSubviewToView(this.view)

    readonly filterAllCompaniesButton = new RHFilterChipButton().configuredWithObject({
        titleLabel: { text: "Kõik minu ettevõtted" },
        enabled: NO
    }).addedAsSubviewToView(this.view)
    _isAllCompaniesScopeActive = NO
    _hasAppliedDefaultCompanyScope = NO
    
    readonly dataView = new RHDataView<EventLogRow>()
        .configuredWithObject({
            noDataMessage: "Minu tegevused puuduvad",
            descriptors: [
                CBDataViewCellDescriptorWithObject<EventLogRow>({
                    keyPath: "timestamp",
                    title: "Aeg",
                    allowSorting: true,
                    dataType: "string",
                    initialOrderingState: "descending",
                    titleLabelAlignment: UITextView.textAlignment.left,
                    cellLabelAlignment: UITextView.textAlignment.left,
                    updateCell: (cellView, dataItem) => {
                        cellView.text = dataItem.timestampText
                    }
                }),
                CBDataViewCellDescriptorWithObject<EventLogRow>({
                    keyPath: "eventDescription",
                    title: "Sündmus",
                    allowSorting: false,
                    titleLabelAlignment: UITextView.textAlignment.left,
                    cellLabelAlignment: UITextView.textAlignment.left,
                    updateCell: (cellView, dataItem) => {
                        const fontWeight = dataItem.isSeen ? "" : "600"
                        
                        if (dataItem.procurementId) {
                            configureEventLogActionCell(
                                cellView,
                                dataItem.eventDescription,
                                `Vaata hanget ${dataItem.procurementId}`
                            )
                        }
                        else {
                            cellView.configureWithObject({
                                isAButton: false,
                                hoverText: "",
                                titleLabel: {
                                    text: dataItem.eventDescription,
                                    textColor: RHTables.rowTextColor,
                                    textAlignment: UITextView.textAlignment.left
                                }
                            })
                        }
                        
                        // Unseen rows shown in bold.
                        cellView.titleLabel!.style.fontWeight = fontWeight
                    },
                    buttonWasPressed: (cellView, dataItem, event) => {
                        
                        let route = UIRoute.currentRoute
                            .routeByRemovingComponentNamed(LogViewController.routeComponentName)
                            .routeWithViewControllerComponent(
                                ProcurementDetailsViewController,
                                {
                                    [ProcurementDetailsViewController.ParameterIdentifierName.id]: dataItem.procurementId
                                }
                            )
                        
                        if (dataItem.procurementTab) {
                            route = route.routeBySettingParameterInComponent(
                                ProcurementDetailsViewController.routeComponentName,
                                ProcurementDetailsViewController.ParameterIdentifierName.tab,
                                dataItem.procurementTab
                            )
                        }
                        
                        route.apply()
                        
                    }
                }),
                CBDataViewCellDescriptorWithObject<EventLogRow, RHTableActionButtonView>({
                    keyPath: "",
                    title: "",
                    // "Detailid" is a real centred blue table-action button, matching the
                    // "Vaata" buttons used elsewhere, rather than a plain text link.
                    titleLabelAlignment: UITextView.textAlignment.center,
                    cellLabelAlignment: UITextView.textAlignment.center,
                    initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                        preferredButtonWidth: 92,
                        preferredButtonHeight: 28
                    }),
                    updateCell: (cellView, dataItem) => {
                        cellView.customView.text = "Detailid"
                        cellView.customView.buttonWasPressed = () => {
                            registerCurrentLogEntry(dataItem.rawEntry)
                            UIRoute.currentRoute
                                .routeWithViewControllerComponent(
                                    LogEventViewController,
                                    { [LogEventViewController.ParameterIdentifierName.id]: dataItem.rawEntry._id }
                                )
                                .apply()
                        }
                    }
                })
            ]
        }).addedAsSubviewToView(this.view)
    
    // ── State ─────────────────────────────────────────────────────────────────
    
    _rows: EventLogRow[] = []
    _isLoading = false
    _reloadRequestedWhileLoading = NO
    _unseenCount = 0
    readonly _pageSize = 100
    
    // ── Constructor ───────────────────────────────────────────────────────────
    
    constructor(view: UIView) {
        
        super(view)
        
        this.view.configureWithObject({ backgroundColor: UIColor.whiteColor })
        RHNotificationBadge.configurePageLabel(this.unseenBadgeLabel)
        this.unseenBadgeLabel.hidden = true
        this._applyTableColumnLayout()

        this.filterAllCompaniesButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._isAllCompaniesScopeActive = NO == this._isAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
            void this._loadEvents()
        }
        
    }
    
    // ── Unseen badge ──────────────────────────────────────────────────────────
    
    private _applyTableColumnLayout() {
        this.dataView.tableHeaderView.configureWithObject({
            cellWeights: RHEventLogViewTokens.eventLogColumnWeights
        })
        this.dataView.tableView.configureWithObject({
            cellWeights: RHEventLogViewTokens.eventLogColumnWeights
        })
        this.dataView.tableHeaderView.setNeedsLayout()
        this.dataView.tableView.setNeedsLayout()
    }

    _updateUnseenBadge() {
        // Page-pill "N uut" eemaldatud lihtsustamise käigus — hoia peidetud (lugemata read jäävad rasvaseks).
        this.unseenBadgeLabel.hidden = true
        this.view.setNeedsLayout()
    }
    
    // ── Seen management ───────────────────────────────────────────────────────
    
    /**
     * Called when the event log is opened. Marks every currently loaded entry
     * as seen. Visual state is updated optimistically; the server confirms the
     * final unseenCount in the response.
     */
    _markAllLoadedEntriesSeen() {
        const unseenIds = this._rows.filter(r => !r.isSeen).map(r => r._id)
        if (!unseenIds.length) {
            return
        }
        
        // Optimistic local update
        this._rows.forEach(r => {
            r.isSeen = true
        })
        this._unseenCount = 0
        this._updateUnseenBadge()
        this.dataView.data = [...this._rows]  // Re-render cells so bold is removed
        
        SocketClient.MarkEventLogEntriesAsSeen({
            logIds: unseenIds,
            companyId: RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        }).then(response => {
            // Server is authoritative on the count (may differ if new events arrived)
            this._unseenCount = response.result.unseenCount
            this._updateUnseenBadge()
            RHCore.sharedInstance.notificationStore.updateGlobalCounts(response.result.unseenCounts)
        }).catch(e => {
            console.warn("EventLogViewController: MarkEventLogEntriesAsSeen failed", e)
        })
    }
    
    // ── Data loading ──────────────────────────────────────────────────────────

    _requestedCompanyIdForScope(): string | undefined {
        if (this._isAllCompaniesScopeActive) {
            return undefined
        }
        return RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
    }

    async _refreshCompanyScopeAffordances() {
        await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
        if (!this._hasAppliedDefaultCompanyScope) {
            this._hasAppliedDefaultCompanyScope = YES
            this._isAllCompaniesScopeActive = RHCore.sharedInstance.companyAffiliationStore.defaultAllCompaniesScopeActive
            this.filterAllCompaniesButton.chipActive = this._isAllCompaniesScopeActive
        }
        const hasMultipleCompanies = RHCore.sharedInstance.companyAffiliationStore.hasMultipleCompanyAffiliations
        this.filterAllCompaniesButton.enabled = hasMultipleCompanies
        this.view.setNeedsLayout()
    }
    
    async _loadEvents() {
        if (this._isLoading) {
            this._reloadRequestedWhileLoading = YES
            return
        }
        this._isLoading = true
        this.dataView.loading = YES

        try {
            do {
                this._reloadRequestedWhileLoading = NO
                try {
                    const result = (await SocketClient.RetrieveMyEventLog({
                        limit: this._pageSize,
                        companyId: this._requestedCompanyIdForScope()
                    })).result
                    if (this._reloadRequestedWhileLoading) {
                        continue
                    }
                    this._rows = result.logs.map(flattenEntry)
                    this._unseenCount = result.unseenCount
                    this._updateUnseenBadge()
                    this.dataView.data = [...this._rows]
                    // Mark everything seen now that the user has opened the log
                    this._markAllLoadedEntriesSeen()
                }
                catch (e) {
                    console.error("EventLogViewController: failed to load events", e)
                }
            } while (this._reloadRequestedWhileLoading)
        }
        finally {
            this._isLoading = false
            this.dataView.loading = NO
        }
    }
    
    // ── Lifecycle ─────────────────────────────────────────────────────────────

    async reloadEvents() {
        await this._refreshCompanyScopeAffordances()
        await this._loadEvents()
    }
    
    override async viewDidAppear() {
        await this.reloadEvents()
    }

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.companyAffiliationsDidChange) {
            void this.reloadEvents()
            return
        }
        if (
            event.name === RHCore.broadcastEventName.activeCompanyDidChange &&
            this._isAllCompaniesScopeActive == NO
        ) {
            void this._loadEvents()
        }
    }
    
    override async handleRoute(route: UIRoute) {
        super.handleRoute(route)
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const badgeHeight = RHBadges.notificationPageHeight
        const badgePaddingH = RHBadges.notificationPagePaddingX
        const bounds = this.view.contentBoundsWithInset(padding)
        
        // Title row: header left, unseen badge right-aligned to it
        const titleFrame = bounds.rectangleWithHeight(labelHeight)
        this.titleLabel.frame = titleFrame
        
        if (!this.unseenBadgeLabel.hidden) {
            const badgeWidth = this.unseenBadgeLabel.intrinsicContentWidth(badgeHeight) + badgePaddingH * 2
            this.unseenBadgeLabel.frame = titleFrame
                .rectangleWithWidth(badgeWidth, 1)       // right-align
                .rectangleWithHeight(badgeHeight, 0.5)   // center vertically
            this.unseenBadgeLabel.setBorder(badgeHeight / 2, 0)
        }
        
        titleFrame
            .rectangleForNextRow(padding)
            .IF(this.filterAllCompaniesButton.superview === this.view)
            .rectangleWithHeight(this.filterAllCompaniesButton.chipRowHeight)
            .performingFunctionWithSelf(filterRowFrame => {
                filterRowFrame
                    .rectangleWithWidth(this.filterAllCompaniesButton)
                    .assignedAsFrameOfView(this.filterAllCompaniesButton)
            })
            .rectangleForNextRow(padding)
            .ENDIF()
            .rectangleWithHeight([this.dataView.intrinsicContentHeight(bounds.width), 700].min())
            .assignedAsFrameOfView(this.dataView)
    }
    
}

// ─────────────────────────────────────────────────────────────────────────────
// markProcurementEventsAsSeen
// Call this from ProcurementDetailsViewController.viewDidAppear (or equivalent)
// so that opening a procurement page also clears the unseen badge for any
// of the user's own audit events that relate to that procurement.
// Fire-and-forget — the result is not needed at the call site.
// ─────────────────────────────────────────────────────────────────────────────

export function markProcurementEventsAsSeen(procurementId: string): void {
    SocketClient.MarkEventLogEntriesAsSeen({
        procurementId,
        companyId: RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
    }).then(response => {
        RHCore.sharedInstance.notificationStore.updateGlobalCounts(response.result.unseenCounts)
    }).catch(e => {
        console.warn("markProcurementEventsAsSeen: request failed", e)
    })
}
